import { useState } from "react";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Layers,
  MapPin,
  Sparkles,
  Sticker,
} from "lucide-react";
import SectionFadeIn from "@/components/SectionFadeIn";

const MATERIALS = [
  {
    id: "standard",
    icon: Layers,
    title: "Standard Paste-the-Wall",
    price: 14,
    bestFor: "Most home walls, feature walls, bedrooms, living spaces, and straightforward mural installs.",
    desc: "Our everyday mural option for customers who want a clean, professional wallpaper finish with strong value.",
    points: [
      "Best-value made-to-measure mural material",
      "Clean, classic wallpaper-style finish",
      "Strong option for most residential feature walls",
      "Ideal when you want a professional mural look at the most accessible price point",
    ],
  },
  {
    id: "textured",
    icon: Sparkles,
    title: "Premium Textured",
    price: 18,
    bestFor: "Higher-end interiors, statement walls, and customers who want a richer surface feel.",
    desc: "A more elevated mural material with a premium textured finish for spaces where the wall needs to feel more decorative and design-led.",
    points: [
      "More premium decorative surface",
      "Better suited to luxury interiors and standout walls",
      "Adds extra depth and character to the finished mural",
      "Great choice when the wall is a focal point in the room",
    ],
  },
  {
    id: "peel",
    icon: Sticker,
    title: "Peel & Stick Wallpaper",
    price: 28,
    bestFor: "High-end wall murals, retail interiors, exhibition graphics, temporary spaces, and customers who want easier application with clean removal.",
    desc: "Premium 220gsm self-adhesive removable wallpaper designed for vibrant wall murals, smoother installs, and damage-free removal.",
    points: [
      "Peel & stick application with no paste required",
      "Repositionable during install for easier panel alignment",
      "Removes cleanly with no residue or wall damage",
      "High colour vibrancy with scratch-, water-, and tear-resistant polyester fabric",
      "Best on smooth indoor walls for murals, branding, and display spaces",
    ],
  },
];

const CHOOSE_GUIDE = [
  {
    num: "01",
    title: "Choose Standard Paste-the-Wall if:",
    body: "You want the best-value mural option, a professional wallpaper-style finish, and a strong all-round choice for most rooms.",
  },
  {
    num: "02",
    title: "Choose Premium Textured if:",
    body: "You want the wall to feel more premium, more decorative, and more like a statement design feature in the room.",
  },
  {
    num: "03",
    title: "Choose Peel & Stick if:",
    body: "You want a premium self-adhesive wallpaper that installs with less mess, can be repositioned during fitting, and removes cleanly for modern interiors or temporary applications.",
  },
  {
    num: "04",
    title: "If you are unsure:",
    body: "Start by pricing the wall size first, then compare materials based on budget, finish, and how you want the installation to work. If needed, request a quote and we can help you choose the right option.",
  },
];

function scrollToEstimator() {
  const el = document.getElementById("mural-configurator");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function MuralMaterialsSection() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section
      className="py-20 md:py-28"
      data-testid="mural-materials-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* ── Heading ── */}
        <SectionFadeIn>
          <div className="max-w-2xl">
            <div className="flex items-center gap-2.5 mb-3">
              <MapPin size={14} className="text-brand-gold" />
              <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold">
                Printed Locally in Coleraine
              </p>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-brand-text leading-tight">
              Our Wallpaper &amp; Wall Mural Materials
            </h2>
            <p className="mt-3 text-[14px] text-brand-text-secondary leading-relaxed">
              Premium quality wall mural materials, printed locally in Coleraine. Choose the finish that suits your wall, your room, and how you want the installation to feel.
            </p>
            <p className="mt-3 text-[13px] text-brand-text-secondary/60 leading-relaxed">
              Every wall mural is made to measure and printed for your exact wall size. We offer three core mural material options so customers can choose the right balance of finish, installation style, and room use before they price the job.
            </p>
          </div>
        </SectionFadeIn>

        {/* ── Materials comparison cards ── */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {MATERIALS.map((mat, i) => {
            const Icon = mat.icon;
            return (
              <SectionFadeIn key={mat.id} delay={i * 80}>
                <div
                  className="h-full rounded-sm border border-white/[0.08] bg-white/[0.02] p-6 flex flex-col"
                  data-testid={`material-card-${mat.id}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-brand-gold/20 bg-brand-gold/[0.06]">
                        <Icon size={16} className="text-brand-gold" />
                      </span>
                      <h3 className="text-[15px] font-semibold text-brand-text leading-tight">
                        {mat.title}
                      </h3>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-4 pb-4 border-b border-white/[0.06]">
                    <span className="text-[26px] font-bold text-brand-gold">&pound;{mat.price}</span>
                    <span className="text-[13px] text-brand-text-secondary/50 ml-1">/m&sup2;</span>
                  </div>

                  {/* Best for */}
                  <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-brand-text-secondary/50 mb-1.5">
                    Best for
                  </p>
                  <p className="text-[13px] text-brand-text-secondary leading-relaxed mb-4">
                    {mat.bestFor}
                  </p>

                  {/* Description */}
                  <p className="text-[12px] text-brand-text-secondary/55 leading-relaxed mb-5">
                    {mat.desc}
                  </p>

                  {/* Points */}
                  <div className="mt-auto space-y-2.5">
                    {mat.points.map((pt) => (
                      <div key={pt} className="flex items-start gap-2.5">
                        <Check size={13} className="text-brand-gold shrink-0 mt-0.5" />
                        <span className="text-[12px] text-brand-text-secondary/70 leading-snug">{pt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </SectionFadeIn>
            );
          })}
        </div>

        {/* ── Pricing note ── */}
        <SectionFadeIn>
          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 rounded-sm border border-white/[0.06] bg-white/[0.015] px-6 py-4" data-testid="mural-pricing-note">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-gold/60" />
              <span className="text-[12px] text-brand-text-secondary/60">
                Minimum mural order: <span className="text-brand-text font-medium">&pound;45</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-gold/60" />
              <span className="text-[12px] text-brand-text-secondary/60">
                Optional finish upgrade: <span className="text-brand-text font-medium">Luxury Panel Finish +&pound;12 per order</span>
              </span>
            </div>
          </div>
        </SectionFadeIn>

        {/* ── How to Choose the Right Wallpaper ── */}
        <SectionFadeIn>
          <div className="mt-16 max-w-2xl">
            <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold mb-3">
              Not sure which material?
            </p>
            <h3 className="font-heading text-2xl sm:text-3xl tracking-tight text-brand-text leading-tight">
              How to Choose the Right Wallpaper
            </h3>
          </div>
        </SectionFadeIn>

        <div className="mt-8 max-w-3xl space-y-2" data-testid="mural-choose-guide">
          {CHOOSE_GUIDE.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <SectionFadeIn key={item.num} delay={i * 60}>
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className={`w-full rounded-sm border text-left transition-all duration-200 ${
                    isOpen
                      ? "border-brand-gold/25 bg-brand-gold/[0.04]"
                      : "border-white/[0.06] bg-white/[0.015] hover:border-white/15"
                  }`}
                  data-testid={`choose-guide-${i}`}
                >
                  <div className="flex items-center gap-4 px-5 py-4">
                    <span className={`text-[18px] font-heading leading-none shrink-0 ${isOpen ? "text-brand-gold" : "text-brand-gold/25"}`}>
                      {item.num}
                    </span>
                    <span className={`flex-1 text-[14px] font-medium ${isOpen ? "text-brand-gold" : "text-brand-text"}`}>
                      {item.title}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`shrink-0 transition-transform duration-200 ${
                        isOpen ? "rotate-180 text-brand-gold" : "text-brand-text-secondary/40"
                      }`}
                    />
                  </div>
                  <div
                    className={`overflow-hidden transition-all duration-200 ${
                      isOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <p className="px-5 pb-4 pl-14 text-[13px] text-brand-text-secondary leading-relaxed">
                      {item.body}
                    </p>
                  </div>
                </button>
              </SectionFadeIn>
            );
          })}
        </div>

        {/* ── Estimator teaser + CTA ── */}
        <SectionFadeIn>
          <div className="mt-14 rounded-sm border border-brand-gold/15 bg-[linear-gradient(135deg,rgba(203,160,82,0.04),rgba(10,13,20,0.97))] px-6 py-8 md:px-8 md:py-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6" data-testid="mural-estimator-teaser">
            <div>
              <p className="text-[14px] font-medium text-brand-text">
                Not sure which material is right yet?
              </p>
              <p className="mt-1 text-[13px] text-brand-text-secondary/60 leading-relaxed max-w-lg">
                Use the live mural price estimator to compare your wall size first, then match the best material to the room.
              </p>
            </div>
            <button
              type="button"
              onClick={scrollToEstimator}
              className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand-gold px-7 py-3.5 text-[13px] font-semibold tracking-wide text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover shrink-0"
              data-testid="mural-price-cta"
            >
              Price Your Mural Now <ArrowRight size={15} />
            </button>
          </div>
        </SectionFadeIn>
      </div>
    </section>
  );
}
